export type LegalDocumentKind = "terms" | "privacy";

export type LegalSection = {
  id: string;
  title: string;
  paragraphs: readonly string[];
  bullets?: readonly string[];
};

export type LegalDocumentDefinition = {
  eyebrow: string;
  title: string;
  summary: string;
  reviewed: string;
  sections: readonly LegalSection[];
};

export const legalDocuments: Record<LegalDocumentKind, LegalDocumentDefinition> = {
  terms: {
    eyebrow: "Carevinco legal",
    title: "Terms & Conditions",
    summary:
      "These terms explain the rules for using Carevinco, requesting care, managing bookings, and making payments through our platform.",
    reviewed: "September 2026",
    sections: [
      {
        id: "acceptance",
        title: "1. Acceptance and eligibility",
        paragraphs: [
          "By creating an account, accessing Carevinco, or booking a service, you agree to these Terms & Conditions and our Privacy Policy. If you use Carevinco for another person, you confirm that you are authorized to provide their details and arrange care for them.",
          "You must provide accurate information, be legally able to enter into this agreement, and use the platform only for lawful purposes.",
        ],
      },
      {
        id: "accounts",
        title: "2. Accounts and care information",
        paragraphs: [
          "You are responsible for keeping your login credentials secure and for activity performed through your account. Please tell us promptly if you believe your account has been accessed without permission.",
          "Care requirements, patient details, addresses, availability, and contact information must be complete and current so that care can be arranged safely and accurately.",
        ],
      },
      {
        id: "bookings",
        title: "3. Bookings and care providers",
        paragraphs: [
          "Carevinco helps users request services and connect with suitable care providers. A booking is subject to provider availability, service-area coverage, verification, payment status, and confirmation shown in the platform.",
          "Provider assignment may change when a provider is unavailable, rejects a request, or a reassignment is needed to complete the booking. The latest confirmed status shown in your account applies.",
        ],
      },
      {
        id: "payments",
        title: "4. Pricing, payments, cancellations, and refunds",
        paragraphs: [
          "Prices, taxes, fees, booking duration, and payment details are presented before payment. You authorize the selected payment method to be charged for the confirmed booking and any clearly disclosed additions you approve.",
          "Cancellation and refund eligibility may depend on booking type, slot status, start time, services already delivered, and the cancellation terms displayed during booking. Approved refunds are returned through the applicable payment channel and may take time to appear.",
        ],
      },
      {
        id: "safe-use",
        title: "5. Safe and appropriate use",
        paragraphs: [
          "Treat users, patients, providers, and Carevinco staff respectfully. Do not misuse the platform, submit false information, interfere with service delivery, or use another person's account without permission.",
          "Carevinco is not an emergency response service. For urgent or life-threatening situations, contact the appropriate emergency services immediately.",
        ],
      },
      {
        id: "platform",
        title: "6. Platform availability and content",
        paragraphs: [
          "We work to keep Carevinco available and accurate, but access may occasionally be interrupted for maintenance, security, network issues, or events outside our reasonable control. Features may be improved, replaced, or discontinued when needed.",
          "Carevinco's brand, software, interface, and original platform content are protected by applicable intellectual-property laws and may not be copied or commercially reused without permission.",
        ],
      },
      {
        id: "responsibility",
        title: "7. Responsibility and account action",
        paragraphs: [
          "To the extent permitted by law, each party remains responsible for losses caused by its own unlawful conduct, negligence, or breach of these terms. Nothing in these terms removes rights that cannot legally be excluded.",
          "We may restrict or close an account when necessary to protect users, providers, the platform, or legal compliance. Where appropriate, we will provide notice and a reasonable way to contact support.",
        ],
      },
      {
        id: "updates",
        title: "8. Changes and contact",
        paragraphs: [
          "We may update these terms to reflect changes in our services, law, or safety practices. The current version and its effective date will be published on this page.",
          "Questions about these terms can be sent to Carevinco support using the contact details below.",
        ],
      },
    ],
  },
  privacy: {
    eyebrow: "Your information",
    title: "Privacy Policy",
    summary:
      "This policy explains what personal information Carevinco handles, why we use it, and the choices available to users and care providers.",
    reviewed: "September 2026",
    sections: [
      {
        id: "information",
        title: "1. Information we collect",
        paragraphs: [
          "We collect information you provide when creating an account, managing a profile, adding a patient or dependent, requesting care, contacting support, or completing a booking.",
        ],
        bullets: [
          "Identity and contact details, such as name, phone number, email, age, and address.",
          "Care and booking details, including service needs, schedules, patient information, and provider updates.",
          "Payment references and transaction status received from payment partners.",
          "Device, usage, notification-token, and location information when required permissions are granted.",
        ],
      },
      {
        id: "use",
        title: "2. How we use information",
        paragraphs: [
          "We use personal information to operate accounts, arrange and manage care, match providers, process payments, send booking and safety updates, provide support, prevent misuse, meet legal duties, and improve Carevinco.",
          "Where consent is required, you can choose whether to grant it. Some platform features may not work without information that is essential to deliver the requested service.",
        ],
      },
      {
        id: "sharing",
        title: "3. When information is shared",
        paragraphs: [
          "We share only the information reasonably needed with assigned care providers, patients or authorized family members, payment and communication partners, professional advisers, and authorities when legally required.",
          "We do not sell personal information. Service partners are expected to use information only for the agreed purpose and to protect it appropriately.",
        ],
      },
      {
        id: "location",
        title: "4. Location, devices, and notifications",
        paragraphs: [
          "If you allow location access, Carevinco may use location data to select an address, support navigation, estimate arrival, or enable location-dependent booking features. Device settings can be used to change location access.",
          "Notification tokens and preferences are used to deliver account, booking, provider, payment, and security messages. You can adjust permissions in your device settings, although essential in-app status updates may still appear in your account.",
        ],
      },
      {
        id: "security",
        title: "5. Security and retention",
        paragraphs: [
          "We use reasonable administrative, technical, and organizational safeguards designed to protect personal information. No storage or transmission method can be guaranteed to be completely secure.",
          "Information is retained only for as long as needed for care operations, account support, financial records, safety, dispute handling, and legal obligations, then deleted or de-identified where appropriate.",
        ],
      },
      {
        id: "choices",
        title: "6. Your choices and rights",
        paragraphs: [
          "Depending on applicable law, you may ask to access, correct, update, or delete certain personal information. You can also manage app permissions and communication preferences from your account or device settings.",
          "Some records may need to be retained where required by law or where they are necessary to complete transactions, protect safety, prevent fraud, or resolve disputes.",
        ],
      },
      {
        id: "dependents",
        title: "7. Patient and dependent information",
        paragraphs: [
          "When you submit information about a patient, child, older adult, or dependent, you confirm that you have the authority or appropriate consent to provide that information and use it to arrange care.",
          "Please share only information that is relevant to safe service delivery and keep it accurate throughout the care relationship.",
        ],
      },
      {
        id: "policy-updates",
        title: "8. Policy updates and contact",
        paragraphs: [
          "We may update this policy when our services, technology, or legal obligations change. The current version and effective date will be available on this page.",
          "For privacy questions or requests, contact Carevinco support using the details below.",
        ],
      },
    ],
  },
};
